import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Users, Shield, ChefHat } from 'lucide-react';

interface Worker {
  id: string;
  name: string;
  description: string;
  role: string;
  image_url: string;
  is_active: boolean;
}

const Team: React.FC = () => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    try {
      const { data, error } = await supabase
        .from('workers')
        .select('*')
        .eq('is_active', true)
        .order('name')
        .limit(8); // Mostrar máximo 8 trabajadores

      if (error) throw error;
      setWorkers(data || []);
    } catch (error) {
      console.error('Error loading workers:', error);
      setWorkers([]); // En caso de error, mostrar array vacío
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    const roleLower = role.toLowerCase();
    if (roleLower.includes('propietario') || roleLower.includes('dueño')) {
      return <Shield className="w-4 h-4 text-purple-600" />;
    } else if (roleLower.includes('admin') || roleLower.includes('administrador') || roleLower.includes('encargado')) {
      return <Shield className="w-4 h-4 text-blue-600" />;
    } else if (roleLower.includes('preparador') || roleLower.includes('carnes')) {
      return <ChefHat className="w-4 h-4 text-orange-600" />;
    } else if (roleLower.includes('vigilante')) {
      return <Shield className="w-4 h-4 text-green-600" />;
    } else {
      return <Users className="w-4 h-4 text-gray-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (workers.length === 0) {
    return null; // No mostrar la sección si no hay trabajadores
  }

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-100 to-pink-100 text-red-800 px-6 py-3 rounded-full shadow-lg mb-8">
            <Users className="w-5 h-5" />
            <span className="font-bold text-sm md:text-base">Nuestro Equipo</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Conoce a Nuestro Equipo
          </h2>

          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Un equipo apasionado y comprometido que trabaja día a día para ofrecerte
            la mejor experiencia en Mundo Carne.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {workers.map((worker) => (
            <div
              key={worker.id}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 text-center border border-gray-100 hover:border-red-200"
            >
              <div className="relative mb-4">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center overflow-hidden shadow-lg group-hover:scale-105 transition-transform duration-300">
                  {worker.image_url ? (
                    <img
                      src={worker.image_url}
                      alt={worker.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-red-600 font-bold text-xl">
                      {worker.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
                  {getRoleIcon(worker.role)}
                </div>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-red-600 transition-colors">
                {worker.name}
              </h3>

              <p className="text-sm font-medium text-gray-600 mb-2">
                {worker.role}
              </p>

              {worker.description && (
                <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                  {worker.description}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-4 bg-white rounded-2xl px-8 py-4 shadow-lg border border-gray-100">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{workers.length}</div>
              <div className="text-sm font-medium text-gray-600">Miembros del Equipo</div>
            </div>
            <div className="h-8 w-px bg-gray-300" />
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {workers.filter(w => w.role.toLowerCase().includes('admin') || w.role.toLowerCase().includes('administrador') || w.role.toLowerCase().includes('encargado')).length}
              </div>
              <div className="text-sm font-medium text-gray-600">Administrativos</div>
            </div>
            <div className="h-8 w-px bg-gray-300" />
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {workers.filter(w => w.role.toLowerCase().includes('preparador') || w.role.toLowerCase().includes('carnes')).length}
              </div>
              <div className="text-sm font-medium text-gray-600">Preparadores</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Team;